import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { form, max, maxLength, min, minLength, required } from "@angular/forms/signals";
import { ActivatedRoute, Router } from "@angular/router";
import { StrategyStateEnum, type TacosStrategyCreateRequest, TacosStrategyGroup } from "@front/m19-api-client";
import { TacosStrategiesService } from "@front/m19-services";
import { IButtonDirective, IStepperComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import {
  TacosCreateStrategyAsinsComponent,
  TacosCreateStrategyAsinsModel,
} from "@m19-board/strategies/tacos-strategy/steps/tacos-create-strategy-asins.component";
import {
  TacosCreateStrategySetupComponent,
  TacosCreateStrategySetupModel,
} from "@m19-board/strategies/tacos-strategy/steps/tacos-create-strategy-setup.component";
import { finalize, take } from "rxjs";

interface TacosStrategyCreationFormModel {
  setup: TacosCreateStrategySetupModel;
  asins: TacosCreateStrategyAsinsModel;
}

@Component({
  selector: "app-create-tacos-strategy",
  templateUrl: "./create-tacos-strategy.component.html",
  providers: [FormNavigationService],
  imports: [
    TranslocoDirective,
    IButtonDirective,
    IStepperComponent,
    FormShellComponent,
    TacosCreateStrategySetupComponent,
    TacosCreateStrategyAsinsComponent,
  ],
  host: {
    class: "block h-full min-h-0",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTacosStrategyComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly formNavigationService = inject(FormNavigationService<TacosStrategyCreationFormModel>);

  formModel = signal<TacosStrategyCreationFormModel>({
    setup: {
      name: "",
      tacosTarget: null,
    },
    asins: {
      asins: [],
    },
  });

  creationForm = form(
    this.formModel,
    (schema) => {
      required(schema.setup.name);
      maxLength(schema.setup.name, 80);

      required(schema.setup.tacosTarget);
      min(schema.setup.tacosTarget, 1);
      max(schema.setup.tacosTarget, 100);

      required(schema.asins.asins);
      minLength(schema.asins.asins, 1);
    },
    {
      submission: {
        action: async () => this.submitForm(),
      },
    },
  );

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;

  isCreatingStrategy = signal(false);

  ngOnInit(): void {
    this.formNavigationService.configure({
      form: this.creationForm,
      blocked: this.isCreatingStrategy,
      steps: [
        {
          stepId: "setup",
          label: this.translocoService.translate("common.setup"),
          size: "sm",
        },
        {
          stepId: "asins",
          label: this.translocoService.translate("common.asins"),
          size: "md",
        },
      ],
    });
  }

  goToStep(stepId: IFormStep<TacosStrategyCreationFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  cancel() {
    void this.router.navigate([".."], { relativeTo: this.activatedRoute });
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    this.formNavigationService.next();
  }

  submitForm() {
    if (this.creationForm().invalid()) {
      return;
    }

    this.isCreatingStrategy.set(true);

    const formValue = this.creationForm().value();
    const requestBody: TacosStrategyCreateRequest = {
      name: formValue.setup.name,
      tacosTarget: formValue.setup.tacosTarget! / 100,
      state: StrategyStateEnum.ENABLED,
      asins: formValue.asins.asins,
    };

    this.tacosStrategiesService
      .createTacosStrategyGroup(requestBody)
      .pipe(
        take(1),
        finalize(() => this.isCreatingStrategy.set(false)),
      )
      .subscribe({
        next: (s: TacosStrategyGroup) => {
          void this.router.navigate(["/advertising/tacos-strategies/", s.tacosStrategyGroupId]);
        },
        error: (err) => {
          this.toasterService.error(err, "Error creating strategy");
        },
      });
  }
}
